import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useEffect, useState } from 'react';
import NavBar from '../../components/NavBar/NavBar';
import UserModalProps from './userModal';
import { getUsers, updateUser } from '../../api/authApi';
import { useAppDialog } from '../../Hooks/useAppDialog';

const emptyPassword = {
  password: '',
  confirmPassword: '',
};

const emptyUser = {} as UserModalProps;

const LOCATION_OPTIONS = ['DC-Caledonia', 'DC'];
const ROLE_OPTIONS = ['User', 'Admin', 'SuperAdmin'];

const UserRoleAccess: React.FC = () => {
  const [userList, setUserList] = useState<UserModalProps[]>([]);
  const [password, setPassword] = useState(emptyPassword);
  const [userModal, setUserModal] = useState(false);
  const [user, setUser] = useState<UserModalProps>(emptyUser);
  const { dialog, openDialog, closeDialog } = useAppDialog();

  const getData = async () => {
    try {
      const result = await getUsers();
      setUserList(result || []);
    } catch (err) {
      console.log('Error fetching users:', err);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleModify = (selectedUser: UserModalProps) => {
    setUser(selectedUser);
    setPassword(emptyPassword);
    setUserModal(true);
  };

  const handleCloseModal = () => {
    setUserModal(false);
    setUser(emptyUser);
    setPassword(emptyPassword);
  };

  const handleSave = async () => {
    const pwd =
      password.password &&
        password.confirmPassword &&
        password.password === password.confirmPassword
        ? password.password
        : undefined;

    try {
      await updateUser({
        ...user,
        password: pwd,
      });

      setUserList((prev) =>
        prev.map((item) =>
          item.email === user.email
            ? {
              ...item,
              first_name: user.first_name,
              last_name: user.last_name,
              location: user.location,
              scope: user.scope,
            }
            : item
        )
      );

      handleCloseModal();

      openDialog({
        variant: 'success',
        title: 'User Updated',
        message: 'User updated successfully.',
        confirmText: 'Okay',
        hideCancel: true,
      });
    } catch (error: any) {
      console.log(error);

      openDialog({
        variant: 'error',
        title: 'Update Failed',
        message: error?.message || `Error updating user: ${user.first_name}`,
        confirmText: 'Close',
        hideCancel: true,
      });
    }
  };

  return (
    <>
      <NavBar />

      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              User Role Access
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and update user roles, location access, and account details.
            </Typography>
          </Box>

          <Card>
            <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
              {userList.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>First Name</TableCell>
                        <TableCell>Last Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {userList.map((userItem, index) => (
                        <TableRow key={`${userItem.email}-${index}`} hover>
                          <TableCell>{userItem.first_name}</TableCell>
                          <TableCell>{userItem.last_name}</TableCell>
                          <TableCell>{userItem.email}</TableCell>
                          <TableCell>{userItem.location}</TableCell>
                          <TableCell>{userItem.scope}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleModify(userItem)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight={600}>
                    No users found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    User records will appear here once available.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Dialog
        open={userModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Modify User</DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={user.email || ''}
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={user.first_name || ''}
                  placeholder="Enter first name..."
                  onChange={(e) =>
                    setUser({ ...user, first_name: e.target.value || '' })
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={user.last_name || ''}
                  placeholder="Enter last name..."
                  onChange={(e) =>
                    setUser({ ...user, last_name: e.target.value || '' })
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Location"
                  value={user.location || ''}
                  onChange={(e) =>
                    setUser({ ...user, location: e.target.value || '' })
                  }
                >
                  {LOCATION_OPTIONS.map((location) => (
                    <MenuItem key={location} value={location}>
                      {location}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Scope"
                  value={user.scope || ''}
                  onChange={(e) =>
                    setUser({ ...user, scope: e.target.value as any })
                  }
                >
                  {ROLE_OPTIONS.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password.password}
                  placeholder="Enter password..."
                  onChange={(e) =>
                    setPassword({ ...password, password: e.target.value || '' })
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={password.confirmPassword}
                  placeholder="Confirm password..."
                  onChange={(e) =>
                    setPassword({
                      ...password,
                      confirmPassword: e.target.value || '',
                    })
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserRoleAccess;